import type { INoteObservable, NotePasteClipboardContent } from "@/store/note/types";
import type { TemplateObservable } from "@/store/templates/TemplateObservable";
import { NoteObservable } from "@/store/note/NoteObservable";
import type { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { NoteEditorMode } from "@/components/note/types";
import { useEffect, useRef } from "react";

type NotesViewPageLike =
  | {
      noteVersionHistory?: {
        currentNoteId?: string | null;
      };
    }
  | undefined;

interface UseNoteEditorDisplayStateArgs<
  TNoteObservable extends INoteObservable | TemplateObservable,
> {
  noteObservable: TNoteObservable;
  editorStore?: MemCommonEditorStore;
  mode: NoteEditorMode;
  notesViewPage?: NotesViewPageLike;
}

export interface NoteEditorDisplayState {
  shouldShowBottomBar: boolean;
  shouldShowQuickActions: boolean;
}

export const useNoteEditorDisplayState = <
  TNoteObservable extends INoteObservable | TemplateObservable,
>({
  noteObservable,
  editorStore,
  mode,
  notesViewPage,
}: UseNoteEditorDisplayStateArgs<TNoteObservable>): NoteEditorDisplayState => {
  const isNoteObservable = noteObservable instanceof NoteObservable;
  const hasSupportedSources = isNoteObservable
    ? (noteObservable.sourceList?.allSupportedSources?.length ?? 0) > 0
    : false;

  const isViewingVersionHistory =
    notesViewPage?.noteVersionHistory?.currentNoteId === noteObservable.id;

  const isNoteEmpty = editorStore?.isNoteEmpty ?? false;

  const shouldShowBottomBar =
    isNoteObservable &&
    mode === NoteEditorMode.Editable &&
    !isViewingVersionHistory &&
    (hasSupportedSources || !isNoteEmpty);

  const shouldShowQuickActions = isNoteEmpty && !shouldShowBottomBar;

  return {
    shouldShowBottomBar,
    shouldShowQuickActions,
  };
};

export const useCaptureInitialPasteClipboardContent = ({
  editorStore,
  initialPasteClipboardContent,
  onInitialPasteClipboardContentCaptured,
}: {
  editorStore?: MemCommonEditorStore;
  initialPasteClipboardContent?: NotePasteClipboardContent;
  onInitialPasteClipboardContentCaptured?: () => void;
}) => {
  const capturedInitialPasteNoteIdRef = useRef<string>();

  useEffect(() => {
    // Wait until the editor store is committed before clearing route state; that
    // state also carries the intended autofocus position for paste-created notes.
    if (!editorStore || !initialPasteClipboardContent) return;
    if (capturedInitialPasteNoteIdRef.current === initialPasteClipboardContent.noteId) return;

    capturedInitialPasteNoteIdRef.current = initialPasteClipboardContent.noteId;
    onInitialPasteClipboardContentCaptured?.();
  }, [editorStore, initialPasteClipboardContent, onInitialPasteClipboardContentCaptured]);
};
